import { BaseLayer } from './BaseLayer';
import { ImageAsset, FootageAsset } from '../assets';
import { FootageLayer } from './FootageLayer';

import { latest as LatestVDETypes } from '@libs/waymark-video/video-descriptor-types';
import { matchObservableObjectChange, ObservableObjectChange } from '@libs/util-ts';

export class ImageLayer extends BaseLayer<
  LatestVDETypes.ImageLayer,
  {
    'change:asset': void;
    'change:contentModifications': void;
    'change:adjustments': void;
    'change:backgroundFill': void;
    'change:fillColor': void;
    'change:cropping': void;
    'change:padding': void;
    'change:zoom': void;
    'change:fitMode': void;
    'change:fitFillAlignment': void;
    // BaseLayer events
    'change:visibility': void;
    'change:fillEffectColor': void;
    change: void;
    'change:ignoredReferences': void;
    removed: void;
  }
> {
  static layerType: LatestVDETypes.ImageLayer['ty'] = LatestVDETypes.LayerType.Image;

  onLayerDataChange(change: ObservableObjectChange): boolean {
    let didChange = false;

    if (super.onLayerDataChange(change)) {
      didChange = true;
    }

    if (matchObservableObjectChange(change, 'refId')) {
      this.dispatchEvent('change:asset');
      didChange = true;
    }

    // Determine whether we should emit a change:contentModifications event, which is used as a catch-all for any changes to content modifications.
    let didContentModificationsChange = false;

    if (matchObservableObjectChange(change, 'contentAdjustments')) {
      this.dispatchEvent('change:adjustments');
      didChange = true;
      didContentModificationsChange = true;
    }

    if (matchObservableObjectChange(change, 'contentBackgroundFill')) {
      this.dispatchEvent('change:backgroundFill');
      didChange = true;
      didContentModificationsChange = true;
    }

    if (matchObservableObjectChange(change, 'contentFillColor')) {
      this.dispatchEvent('change:fillColor');
      didChange = true;
      didContentModificationsChange = true;
    }

    if (matchObservableObjectChange(change, 'contentCropping')) {
      this.dispatchEvent('change:cropping');
      didChange = true;
      didContentModificationsChange = true;
    }

    if (matchObservableObjectChange(change, 'contentPadding')) {
      this.dispatchEvent('change:padding');
      didChange = true;
      didContentModificationsChange = true;
    }

    if (matchObservableObjectChange(change, 'contentZoom')) {
      this.dispatchEvent('change:zoom');
      didChange = true;
      didContentModificationsChange = true;
    }

    if (matchObservableObjectChange(change, 'contentFit')) {
      this.dispatchEvent('change:fitMode');
      didChange = true;
      didContentModificationsChange = true;
    }

    if (matchObservableObjectChange(change, 'contentFitFillAlignment')) {
      this.dispatchEvent('change:fitFillAlignment');
      didChange = true;
      didContentModificationsChange = true;
    }

    if (didContentModificationsChange) {
      this.dispatchEvent('change:contentModifications');
    }

    return didChange;
  }

  /**
   * Creates a new ImageLayer from a FootageLayer.
   *
   * @param footageLayer - The FootageLayer to base the new ImageLayer on
   * @param initialImageAsset - The image asset to use for the new ImageLayer
   */
  static fromFootageLayer(footageLayer: FootageLayer, initialImageAsset: ImageAsset): ImageLayer {
    const { rawLayerData: rawFootageLayerData, videoDescriptor } = footageLayer;

    // Ensure the new image asset is added
    videoDescriptor.addAsset(initialImageAsset);

    // Update the layer data in-place to become a video layer
    const rawLayerData: LatestVDETypes.ImageLayer = {
      ...rawFootageLayerData,
      ty: LatestVDETypes.LayerType.Image,
      refId: initialImageAsset.getID(),
    };

    return new ImageLayer(rawLayerData, footageLayer.parentComposition);
  }

  /**
   * Replaces this layer with a footage layer in the video descriptor, using the provided footage asset.
   * Returns the new footage layer.
   */
  changeToFootageLayer(initialFootageAsset: FootageAsset): FootageLayer {
    const footageLayer = FootageLayer.fromImageLayer(this, initialFootageAsset);
    this.parentComposition.replaceLayer(this, footageLayer);
    return footageLayer;
  }

  getDimensions() {
    return {
      width: this.rawLayerData.w,
      height: this.rawLayerData.h,
    };
  }

  getAssetID() {
    return this.rawLayerData.refId;
  }

  /**
   * Get the image asset associated with this layer.
   */
  getAsset(): ImageAsset {
    const asset = this.videoDescriptor.getAssetByID(this.getAssetID());
    if (!(asset instanceof ImageAsset)) {
      throw new Error(`${this.toString()}: Asset ${this.getAssetID()} is not an image`);
    }
    return asset;
  }

  private updateAssetRefID(newAssetRefID: string) {
    // If we're changing the asset for an image layer, we're going to make an opinionated decision
    // that all "coordinates" of cropping information (zoom level, cropX, cropY, etc.) should be reset.
    // However, we are going to maintain two cropping properties: contentFit and contentFitFillAlignment.
    // The theory here, is that `contentFit` is most influenced by the properties of the layer instead of the asset. `contentFitFillAlignment`
    // is specific to contentFit: 'fill'; so we're going to maintain that as well.
    Object.assign(this.rawLayerData, {
      refId: newAssetRefID,
      // Reset all modifications to defaults
      contentCropping: ImageLayer.DEFAULT_CROPPING,
      contentZoom: ImageLayer.DEFAULT_ZOOM,
      contentPadding: ImageLayer.DEFAULT_PADDING,
      contentAdjustments: undefined,
      contentBackgroundFill: undefined,
      contentFillColor: undefined,
    });
  }

  /**
   * Sets a new image asset for this layer.
   * Resets all modifications on the layer to defaults.
   */
  setAsset(newAsset: ImageAsset, { shouldOverrideReference = false } = {}) {
    const oldAsset = this.getAsset();

    // Ensure the asset is in the manifest
    this.videoDescriptor.addAsset(newAsset);

    if (shouldOverrideReference) {
      this.updateIgnoredReferences({
        imageContent: true,
      });
    }

    if (this.imageContentReference && !this.getIsReferenceIgnored('imageContent')) {
      // Update the image asset reference instead of directly updating this layer;
      // that will trigger the reference's change:value event, which will then come back around
      // to update this layer with any other layers pointing to the same reference
      this.imageContentReference.setImageAsset(newAsset);
    } else {
      this.updateAssetRefID(newAsset.getID());

      // Clean up the old asset if it's no longer in use
      this.videoDescriptor.removeAsset(oldAsset);
    }
  }

  async getAssetURL(): Promise<URL> {
    return await this.getAsset().location.getURL();
  }

  onImageContentReferenceChange() {
    if (!this.imageContentReference || this.getIsReferenceIgnored('imageContent')) {
      return;
    }

    const newImageAsset = this.imageContentReference.getImageAsset();
    this.updateAssetRefID(newImageAsset.getID());
  }

  /** ~~ Modifications ~~ */

  /** contentAdjustments modification */
  static DEFAULT_ADJUSTMENTS: LatestVDETypes.ImageAdjustments = {};

  getImageAdjustments(): LatestVDETypes.ImageAdjustments {
    return {
      // Spread to sanitize so the returned object won't edit the original if modified
      ...(this.rawLayerData.contentAdjustments ?? ImageLayer.DEFAULT_ADJUSTMENTS),
    };
  }

  setImageAdjustments(newAdjustments: Partial<LatestVDETypes.ImageAdjustments> | null) {
    this.rawLayerData.contentAdjustments = newAdjustments
      ? {
          // Apply the new adjustments on top of the base defaults
          ...ImageLayer.DEFAULT_ADJUSTMENTS,
          ...newAdjustments,
        }
      : undefined;
  }

  /** contentBackgroundFill modification */
  // Default color is transparent
  static DEFAULT_BACKGROUND_FILL_COLOR = '#FFFFFF00';

  getBackgroundFillColor(): string {
    return this.rawLayerData.contentBackgroundFill ?? ImageLayer.DEFAULT_BACKGROUND_FILL_COLOR;
  }

  setBackgroundFillColor(newBGFillColor: string | null) {
    this.rawLayerData.contentBackgroundFill = newBGFillColor ?? undefined;
  }

  /** contentCropping modification */
  static DEFAULT_CROPPING: LatestVDETypes.ContentCropping = {
    x: 0,
    y: 0,
    width: 1,
    height: 1,
  };

  getCropping(): LatestVDETypes.ContentCropping {
    return {
      // Spread to sanitize so the returned object won't edit the original if modified
      ...(this.rawLayerData.contentCropping ?? ImageLayer.DEFAULT_CROPPING),
    };
  }

  setCropping(newCroppingData: Partial<LatestVDETypes.ContentCropping>) {
    if (this.getContentFitMode() !== LatestVDETypes.AssetModificationFits.Fill) {
      throw new Error('Cannot set cropping on a layer with contentFit mode other than "fill"');
    }
    this.rawLayerData.contentCropping = {
      ...this.getCropping(),
      ...newCroppingData,
    };
  }

  /** contentFillColor modification */
  // Default color is transparent
  static DEFAULT_FILL_COLOR = '#FFFFFF00';

  getFillColor(): string {
    return this.rawLayerData.contentFillColor ?? ImageLayer.DEFAULT_FILL_COLOR;
  }

  /** NOTE: This is an odd property. I think it's technically supported in after effects, however it shouldn't be "contentFillColor" it should just the fill color of
   * the standard bodymovin properties. This is probably something that has just been cargo-culted along and actually `contentBackgroundFillColor` is the only thing that matters.
   * @migrationtodo
   */
  setFillColor(newFillColor: string | null) {
    this.rawLayerData.contentFillColor = newFillColor ?? undefined;
  }

  /** contentFit modification */
  static DEFAULT_CONTENT_FIT_MODE: `${LatestVDETypes.AssetModificationFits.Fill}` = 'fill';

  getContentFitMode(): `${LatestVDETypes.AssetModificationFits}` {
    return this.rawLayerData.contentFit ?? ImageLayer.DEFAULT_CONTENT_FIT_MODE;
  }

  setContentFitMode(newFitMode: LatestVDETypes.AssetModificationFits) {
    Object.assign(this.rawLayerData, {
      contentFit: newFitMode,
      // Clear fit-mode-dependent modifications
      contentPadding: undefined,
      contentBackgroundFill: undefined,
      contentZoom: undefined,
      contentCropping: undefined,
    });
  }

  /** contentFitFillAlignment modification */
  static DEFAULT_FIT_FILL_ALIGNMENT: `${LatestVDETypes.FitFillAlignment.CenterCenter}` = 'CC';

  getFitFillAlignment(): `${LatestVDETypes.FitFillAlignment}` {
    return this.rawLayerData.contentFitFillAlignment ?? ImageLayer.DEFAULT_FIT_FILL_ALIGNMENT;
  }
  setFitFillAlignment(newFitFillAlignment: LatestVDETypes.FitFillAlignment) {
    this.rawLayerData.contentFitFillAlignment = newFitFillAlignment;
  }

  /** contentPadding modification */
  static DEFAULT_PADDING = 0;

  getPadding(): number {
    return this.rawLayerData.contentPadding ?? ImageLayer.DEFAULT_PADDING;
  }
  setPadding(newPadding: number) {
    this.rawLayerData.contentPadding = newPadding;
  }

  /** contentZoom modification */
  static DEFAULT_ZOOM: LatestVDETypes.ContentZoom = {
    x: 0.5,
    y: 0.5,
    z: 1,
  };

  static DEFAULT_ZOOM_MOTION_EFFECT: LatestVDETypes.ContentZoomMotionEffect = {
    from: {
      x: 0.25,
      y: 0.5,
      z: 1,
    },
    to: {
      x: 0.75,
      y: 0.5,
      z: 1.5,
    },
    ease: 'ease',
  };

  getZoom(): LatestVDETypes.ContentZoom | LatestVDETypes.ContentZoomMotionEffect {
    return {
      // Spread to sanitize so the returned object won't edit the original if modified
      ...(this.rawLayerData.contentZoom ?? ImageLayer.DEFAULT_ZOOM),
    };
  }

  setZoom(newZoomData: LatestVDETypes.ContentZoom | LatestVDETypes.ContentZoomMotionEffect) {
    this.rawLayerData.contentZoom = {
      ...newZoomData,
    };
  }

  cleanUp(): void {
    super.cleanUp();
    // Clean up this layer's asset
    try {
      const asset = this.getAsset();
      this.videoDescriptor.removeAsset(asset);
    } catch {
      // Swallow errors if the asset was not found; we don't care if we're cleaning up the layer
    }
  }
}
